import { Alert, Button, Form, Input, message, Typography } from 'antd';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TUTTI_V2_NAVIGATION } from '@/navigation/tuttiV2';
import {
  type ComputerReleaseDraft,
  releaseDraftToUpdate,
} from '@/services/computerReleaseNotesClient';
import { productUpdatesApi } from '@/services/productUpdatesApi';
import { ComputerReleaseDraftPicker } from './ComputerReleaseDraftPicker';

// This rollout sends only to the account explicitly selected for acceptance.
const testRecipientUserId = '2109548694486388736';

interface UpdateForm {
  title: string;
  content: string;
  version?: string;
}

export function ProductUpdatesPage() {
  const [form] = Form.useForm<UpdateForm>();
  const values = Form.useWatch([], form) as Partial<UpdateForm> | undefined;
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [delivered, setDelivered] = useState<number | null>(null);
  const [loadedDraftVersion, setLoadedDraftVersion] = useState('');
  const pending = useRef<{ fingerprint: string; requestId: string } | null>(
    null,
  );
  const sendingRef = useRef(false);

  const applyReleaseDraft = (draft: ComputerReleaseDraft) => {
    if (sendingRef.current) return;
    form.setFieldsValue(releaseDraftToUpdate(draft));
    setLoadedDraftVersion(draft.version);
    setError('');
    setDelivered(null);
    pending.current = null;
  };

  const publish = async (input: UpdateForm) => {
    if (sendingRef.current) return;
    const body = {
      title: input.title.trim(),
      content: input.content.trim(),
      version: input.version?.trim() ?? '',
      recipientUserIds: [testRecipientUserId],
    };
    const fingerprint = JSON.stringify(body);
    if (pending.current?.fingerprint !== fingerprint) {
      pending.current = { fingerprint, requestId: crypto.randomUUID() };
    }
    sendingRef.current = true;
    setSending(true);
    setError('');
    setDelivered(null);
    try {
      const result = await productUpdatesApi.publish({
        ...body,
        requestId: pending.current.requestId,
      });
      setDelivered(result.recipientCount);
      message.success('更新消息已发布');
    } catch (cause) {
      setError(
        cause instanceof Error
          ? `${cause.message}。可以保持内容不变后重试，已送达的消息不会重复创建。`
          : '发布失败，请保持内容不变后重试。',
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">更新消息</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          向当前指定的测试账号发布更新消息，可在 Tutti 左下角的小铃铛中查看。
        </p>
        <Link
          to={`/${TUTTI_V2_NAVIGATION.routes.computerReleaseNotes}`}
          className="mt-3 inline-block text-sm underline underline-offset-4"
        >
          编辑 Computer 发布信息与查看发布状态
        </Link>
      </div>
      <ComputerReleaseDraftPicker
        disabled={sending}
        hasContent={Boolean(
          values?.title || values?.content || values?.version,
        )}
        onApply={applyReleaseDraft}
      />
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(280px,2fr)]">
        <section
          className="rounded-lg border bg-white p-6"
          aria-label="编写更新消息"
        >
          {loadedDraftVersion ? (
            <output className="mb-5 block text-sm text-muted-foreground">
              已载入 Computer {loadedDraftVersion}{' '}
              草稿。编辑仅保留在当前页面，确认后发布给测试账号。
            </output>
          ) : null}
          <Form
            form={form}
            layout="vertical"
            onFinish={publish}
            disabled={sending}
          >
            <Form.Item
              label="测试接收账号 ID"
              htmlFor="product-update-test-recipient"
              extra="本阶段仅向此账号发送，服务端也会校验该限制。"
            >
              <Input
                id="product-update-test-recipient"
                value={testRecipientUserId}
                readOnly
              />
            </Form.Item>
            <Form.Item name="version" label="版本标记（可选）">
              <Input maxLength={64} />
            </Form.Item>
            <Form.Item
              name="title"
              label="标题"
              rules={[
                { required: true, whitespace: true, message: '请填写消息标题' },
                { max: 120, message: '请将标题编辑到 120 字以内后发布' },
              ]}
            >
              <Input maxLength={120} showCount />
            </Form.Item>
            <Form.Item
              name="content"
              label="更新内容"
              rules={[
                { required: true, whitespace: true, message: '请填写更新内容' },
                { max: 8000, message: '请将更新内容编辑到 8000 字以内后发布' },
              ]}
            >
              <Input.TextArea
                autoSize={{ minRows: 8, maxRows: 18 }}
                maxLength={8000}
                showCount
              />
            </Form.Item>
            {error ? (
              <Alert
                className="mb-5"
                type="error"
                showIcon
                title="消息未确认发布"
                description={error}
              />
            ) : null}
            {delivered !== null ? (
              <Alert
                className="mb-5"
                type="success"
                showIcon
                title={`消息已写入 ${delivered} 个账号的通知中心`}
              />
            ) : null}
            <Button
              className="!border-neutral-900 !bg-neutral-900 !text-white hover:!bg-neutral-800"
              type="primary"
              htmlType="submit"
              loading={sending}
            >
              发布更新消息
            </Button>
          </Form>
        </section>
        <section
          className="rounded-lg border bg-white p-6"
          aria-label="用户通知预览"
        >
          <h2 className="text-base font-medium">用户通知预览</h2>
          {values?.title?.trim() || values?.content?.trim() ? (
            <div className="mt-5 min-w-0 space-y-3">
              <Typography.Text type="secondary">
                Tutti 更新
                {values.version?.trim() ? ` · ${values.version.trim()}` : ''}
              </Typography.Text>
              <h3 className="break-words text-base font-medium">
                {values.title?.trim()}
              </h3>
              <p className="whitespace-pre-wrap break-words text-sm leading-6">
                {values.content?.trim()}
              </p>
            </div>
          ) : (
            <p className="mt-5 text-sm text-muted-foreground">
              填写标题和正文后，在这里预览通知内容。
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
